import { cn } from '../../utils/utils';

interface ToggleProps {
    /** Current on/off state. */
    checked: boolean;
    onChange: (checked: boolean) => void;
    /** Primary label shown next to the switch. */
    label: string;
    /** Optional helper text below the label. */
    description?: string;
    disabled?: boolean;
    className?: string;
}

/**
 * Labeled boolean switch styled to match SelectField/InputField. Plain
 * accessible button with role="switch"; the "on" track uses the tenant
 * primary, the "off" track a neutral gray token. No new dependency.
 */
const Toggle = ({
    checked,
    onChange,
    label,
    description,
    disabled = false,
    className,
}: Readonly<ToggleProps>) => (
    <div className={cn('flex items-start justify-between gap-4', className)}>
        <div className={'flex flex-col'}>
            <span className={'text-sm font-medium text-foreground'}>{label}</span>
            {description && (
                <span className={'mt-0.5 text-xs text-foreground/60'}>{description}</span>
            )}
        </div>
        <button
            type={'button'}
            role={'switch'}
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cn(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full',
                'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                checked ? 'bg-primary' : 'bg-foreground/20',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <span
                className={cn(
                    'inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                    checked ? 'translate-x-5' : 'translate-x-0.5',
                )}
            />
        </button>
    </div>
);

export default Toggle;
